import { alpha, Theme } from '@mui/material/styles';

/**
 * The selected state used to be unreadable on the dark palette.
 *
 * `backgroundColor` was `info.lighter` (#D0F2FF) in BOTH modes while the label flipped to
 * `grey[0]` (#FFFFFF) in dark — white on pale blue, a contrast ratio of **1.18:1**. The background
 * never got a dark-mode value; only the text did, and in the wrong direction. It showed up on every
 * segmented control in the app, including both of the ones in Empyrean's File Settings.
 *
 * Dark mode now tints the surface instead of painting a bright pill on it: a translucent `info.main`
 * over the dark paper, with the pale blue as the LABEL. That keeps the same "tinted selection"
 * language as light mode, reads at roughly 8.7:1, and doesn't glare.
 *
 * (Dark text on the existing pale background would also have been readable — about 12:1 — but a
 * bright pill in a dark UI is the other half of the problem.)
 */
export default function ToggleButton(theme: Theme) {
  const isLight = theme.palette.mode === 'light';

  return {
    MuiToggleButton: {
      styleOverrides: {
        root: {
          // grey[500] on the dark paper is 5.2:1 — passing but muted; grey[400] lifts the
          // unselected options to ~8.9:1 without letting them compete with the selected one
          color: theme.palette.grey[isLight ? 500 : 400],
          border: `solid 1px ${theme.palette.grey[500_32]}`,
          '&.Mui-selected': {
            color: isLight ? theme.palette.grey[600] : theme.palette.info.lighter,
            backgroundColor: isLight ? theme.palette.info.lighter : alpha(theme.palette.info.main, 0.24)
          },
          '&.Mui-disabled': {
            color: theme.palette.grey[500_48]
          }
        }
      }
    }
  };
}
